import { pdfCspSources } from '@edgevanta/pdf/csp';

import penv from './env/client';

// For Vercel deployments, VERCEL_ENV distinguishes between production and preview/staging.
// For local development, NODE_ENV is 'development'.
// If VERCEL_ENV is not set but NODE_ENV === 'production', we treat this as production.
// This handles both Vercel deployments and potential non-Vercel production environments.
const isProd =
  process.env.VERCEL_ENV === 'production' ||
  (process.env.NODE_ENV === 'production' && !process.env.VERCEL_ENV);

const storageUrl = new URL(penv.NEXT_PUBLIC_STORAGE_URL);
const storageOrigin = storageUrl.origin;

const filesCdnOrigin = penv.NEXT_PUBLIC_FILES_CDN_URL
  ? new URL(penv.NEXT_PUBLIC_FILES_CDN_URL).origin
  : '';

const sentryIngestOrigin = (() => {
  if (!penv.NEXT_PUBLIC_SENTRY_DSN) {
    return '';
  }

  try {
    return new URL(penv.NEXT_PUBLIC_SENTRY_DSN).origin;
  } catch {
    return '';
  }
})();

const s3Domains = ['*.s3.amazonaws.com', '*.amazonaws.com'];
// What the PDF viewer needs, declared by the package that needs it. No
// third-party origin: PDFium's wasm ships inside @edgevanta/pdf.
const pdfCsp = pdfCspSources();

const TURNSTILE_ORIGIN = 'https://challenges.cloudflare.com';

// PostHog, addressed DIRECTLY. It used to ride on `'self'` via the `/ingest`
// rewrite; with the reverse proxy gone (see next.config.ts) these origins have
// to be named or every beacon is silently dropped by CSP.
//   us.i.posthog.com          event ingestion + decide
//   us-assets.i.posthog.com   lazily fetched extensions (session recording,
//                             surveys, toolbar) — script AND connect
// `||`, not `??`. The schema is `z.string().optional()`, so
// `NEXT_PUBLIC_POSTHOG_HOST=` validates as the empty string and `?? ` does not
// fire for it — `posthogOrigins` became `['', assets]` and connect-src gained
// only whitespace, with no console error and no Sentry event.
//
// Normalised through `new URL().origin` like filesCdnOrigin and
// sentryIngestOrigin above, so a trailing path or a missing scheme cannot
// produce a malformed CSP source. A value that will not parse falls back
// rather than emitting garbage.
export const POSTHOG_INGEST_ORIGIN = (() => {
  const raw = penv.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
  try {
    return new URL(raw).origin;
  } catch {
    return 'https://us.i.posthog.com';
  }
})();

// posthog-js derives its assets host from `api_host`, so this must follow the
// ingest origin rather than be pinned to US. Hardcoded, a non-US ingest host
// loaded recorder.js / surveys.js / exception-autocapture.js from an origin in
// neither script-src nor connect-src: base beacons kept working while session
// recording and exception capture silently never loaded.
const POSTHOG_ASSETS_ORIGIN = POSTHOG_INGEST_ORIGIN.replace(
  '//us.i.',
  '//us-assets.i.',
).replace('//eu.i.', '//eu-assets.i.');
const posthogOrigins = penv.NEXT_PUBLIC_POSTHOG_KEY
  ? [POSTHOG_INGEST_ORIGIN, POSTHOG_ASSETS_ORIGIN]
  : [];
// Vercel's preview toolbar. Never in production.
const vercelLive = isProd ? [] : ['https://vercel.live'];
const vercelLiveConnect = isProd
  ? []
  : ['https://vercel.live', 'https://*.pusher.com', 'wss://*.pusher.com'];

/** Drops empties so an unset optional origin doesn't leave a double space. */
const directive = (name: string, sources: (string | false | '')[]) =>
  `${name} ${sources.filter(Boolean).join(' ')}`;

// NOTE: untrusted PDFs render as PDFium-WASM in a same-origin Web Worker
// (ADR-009) — no DOM and no cookies, but credentialed same-origin network. That
// is a step down from the opaque-origin PDF.js sandbox it replaced, and the
// containment it rests on now is the hardening in `@edgevanta/pdf`'s
// SECURITY.md, not an origin boundary. The platform is NOT framed to storage/S3
// — frame-src intentionally excludes them; they are reachable over connect-src
// only.
const cspValue = [
  // A floor for every directive not named below (media-src, manifest-src, …),
  // so a new sink defaults to same-origin instead of to anywhere.
  directive('default-src', ["'self'"]),
  directive('frame-ancestors', ["'self'"]),
  directive('frame-src', [
    "'self'",
    TURNSTILE_ORIGIN,
    filesCdnOrigin,
    ...vercelLive,
  ]),
  directive('img-src', [
    "'self'",
    storageOrigin,
    ...s3Domains,
    filesCdnOrigin,
    'data:',
    ...pdfCsp.imgSrc,
  ]),
  directive('connect-src', [
    "'self'",
    storageOrigin,
    ...s3Domains,
    filesCdnOrigin,
    sentryIngestOrigin,
    ...posthogOrigins,
    ...vercelLiveConnect,
    ...pdfCsp.connectSrc,
  ]),
  // NO 'unsafe-eval' IN PRODUCTION. That keyword would reopen pdf.js's
  // `new Function()` font-glyph compiler (CVE-2024-4367) in the app's own
  // origin, where the in-origin EmbedPDF engine runs with the user's session —
  // the single thing this directive exists to prevent. PDFium still compiles,
  // because `pdfCsp.scriptSrc` carries the narrow `'wasm-unsafe-eval'`.
  //
  // Dev needs the broad keyword: Turbopack's HMR and React Refresh evaluate
  // module code through `eval`, so without it the dev server cannot boot.
  //
  // `'unsafe-inline'` is required either way — Next.js emits inline bootstrap
  // and flight-data scripts, and these headers are STATIC (next.config.ts), so
  // there is no per-request nonce to hash them against. Moving the policy into
  // middleware to nonce them is the way to drop it; that is a separate change.
  directive('script-src', [
    "'self'",
    "'unsafe-inline'",
    TURNSTILE_ORIGIN,
    ...posthogOrigins,
    ...(isProd ? [] : ["'unsafe-eval'"]),
    ...vercelLive,
    ...pdfCsp.scriptSrc,
  ]),
  // Tailwind and the component library inject inline <style>.
  directive('style-src', [
    "'self'",
    "'unsafe-inline'",
    ...vercelLive,
    ...pdfCsp.styleSrc,
  ]),
  directive('font-src', ["'self'", 'data:', ...vercelLive]),
  directive('worker-src', pdfCsp.workerSrc),
  // Nothing legitimately loads a plugin, retargets the document base, or posts
  // a form off-origin.
  directive('object-src', ["'none'"]),
  directive('base-uri', ["'self'"]),
  directive('form-action', ["'self'"]),
].join('; ');

export const securityHeaders = [
  {
    key: 'Content-Security-Policy',
    value: cspValue,
  },
  {
    key: 'Cross-Origin-Resource-Policy',
    value: 'cross-origin',
  },
  {
    key: 'Permissions-Policy',
    value: 'camera=(), microphone=(), geolocation=()',
  },
  {
    key: 'Referrer-Policy',
    value: 'no-referrer-when-downgrade',
  },
  {
    key: 'X-Content-Type-Options',
    value: 'nosniff',
  },
  {
    key: 'X-DNS-Prefetch-Control',
    value: 'on',
  },
  {
    key: 'X-Frame-Options',
    value: 'SAMEORIGIN',
  },
  ...(isProd
    ? [
        {
          key: 'Strict-Transport-Security',
          value: 'max-age=31536000; includeSubDomains; preload',
        },
      ]
    : []),
];
